import type {
  AllocationSchedule,
  AppData,
  Asset,
  AssetDocument,
  IssuePayload,
  LoginResponse,
  ReceiptPayload,
  ReturnPayload,
  StocktakePayload,
} from '../types';
import { mockApi } from './mockApi';

const USE_MOCK = String(import.meta.env.VITE_USE_MOCK ?? 'false') === 'true';
const TOKEN_KEY = 'bis-asset-control-token';

const realRequest = async <T,>(action: string, payload: Record<string, unknown> = {}): Promise<T> => {
  const token = localStorage.getItem(TOKEN_KEY) ?? '';
  const response = await fetch('/api', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, payload, token }),
  });
  const result = await response.json() as { ok: boolean; data?: T; error?: string };
  if (!response.ok || !result.ok) throw new Error(result.error || 'Không thể kết nối hệ thống.');
  return result.data as T;
};

export const api = {
  isMock: USE_MOCK,
  login: async (employeeCode: string, password: string): Promise<LoginResponse> => {
    const result = USE_MOCK ? await mockApi.login(employeeCode, password) : await realRequest<LoginResponse>('login', { employeeCode, password });
    localStorage.setItem(TOKEN_KEY, result.token);
    return result;
  },
  logout: async (): Promise<void> => {
    if (USE_MOCK) await mockApi.logout();
    else await realRequest<boolean>('logout');
    localStorage.removeItem(TOKEN_KEY);
  },
  bootstrap: (): Promise<AppData> => USE_MOCK ? mockApi.bootstrap() : realRequest<AppData>('bootstrap'),
  resetDemo: (): Promise<AppData> => mockApi.resetDemo(),
  createReceipt: (payload: ReceiptPayload): Promise<AssetDocument> => USE_MOCK ? mockApi.createReceipt(payload) : realRequest<AssetDocument>('receipts.create', payload as unknown as Record<string, unknown>),
  createAsset: (payload: Omit<Asset, 'id' | 'code' | 'createdAt' | 'allocatedAmount'>): Promise<Asset> => USE_MOCK ? mockApi.createAsset(payload) : realRequest<Asset>('assets.create', payload as unknown as Record<string, unknown>),
  createIssue: (payload: IssuePayload): Promise<AssetDocument> => USE_MOCK ? mockApi.createIssue(payload) : realRequest<AssetDocument>('issues.create', payload as unknown as Record<string, unknown>),
  createReturn: (payload: ReturnPayload): Promise<AssetDocument> => USE_MOCK ? mockApi.createReturn(payload) : realRequest<AssetDocument>('returns.create', payload as unknown as Record<string, unknown>),
  createStocktake: (payload: StocktakePayload): Promise<AssetDocument> => USE_MOCK ? mockApi.createStocktake(payload) : realRequest<AssetDocument>('stocktakes.create', payload as unknown as Record<string, unknown>),
  runAllocation: (month: string): Promise<{ updated: number; amount: number }> => USE_MOCK ? mockApi.runAllocation(month) : realRequest<{ updated: number; amount: number }>('allocations.run', { month }),
};
